import React from "react";
import { motion } from "framer-motion";

/**
 * RevealLines: masked line-by-line reveal for editorial hero copy.
 * Pass `lines` as an array. Each line is wrapped in a clipping mask and
 * translates up/reveal on mount (or when `inView`).
 */
export const RevealLines = ({
  lines = [],
  as: Tag = "h1",
  className = "",
  lineClassName = "",
  delay = 0,
  stagger = 0.09,
  duration = 0.9,
  align = "left",
  onDone,
  ...rest
}) => {
  const MotionTag = motion[Tag] || motion.h1;
  return (
    <MotionTag
      {...rest}
      initial="hidden"
      animate="show"
      onAnimationComplete={onDone}
      variants={{ show: { transition: { staggerChildren: stagger, delayChildren: delay } } }}
      className={className}
      style={{ textAlign: align }}
    >
      {lines.map((line, i) => (
        <span
          key={i}
          className={`block overflow-hidden ${lineClassName}`}
          style={{
            lineHeight: 1.02,
            paddingBottom: "0.22em",
            marginBottom: "-0.22em",
          }}
        >
          <motion.span
            variants={{
              hidden: { y: "110%", opacity: 0 },
              show: {
                y: "0%",
                opacity: 1,
                transition: { duration, ease: [0.22, 1, 0.36, 1] },
              },
            }}
            className="block will-change-transform"
          >
            {line}
          </motion.span>
        </span>
      ))}
    </MotionTag>
  );
};

/**
 * RevealWords: masked word-by-word reveal for shorter statements.
 */
export const RevealWords = ({
  text = "",
  as: Tag = "p",
  className = "",
  delay = 0,
  stagger = 0.03,
  duration = 0.75,
  ...rest
}) => {
  const MotionTag = motion[Tag] || motion.p;
  const words = text.split(" ");
  return (
    <MotionTag
      {...rest}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-80px" }}
      variants={{ show: { transition: { staggerChildren: stagger, delayChildren: delay } } }}
      className={className}
    >
      {words.map((w, i) => (
        <span
          key={i}
          className="inline-block overflow-hidden"
          style={{
            paddingBottom: "0.22em",
            marginBottom: "-0.22em",
            verticalAlign: "top",
          }}
        >
          <motion.span
            variants={{
              hidden: { y: "110%", opacity: 0 },
              show: {
                y: "0%",
                opacity: 1,
                transition: { duration, ease: [0.22, 1, 0.36, 1] },
              },
            }}
            className="inline-block will-change-transform"
          >
            {w}
            {i < words.length - 1 ? "\u00A0" : ""}
          </motion.span>
        </span>
      ))}
    </MotionTag>
  );
};

/**
 * Reveal: generic scroll-triggered fade+rise reveal.
 */
export const Reveal = ({
  children,
  delay = 0,
  y = 24,
  className = "",
  once = true,
  duration = 0.9,
}) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once, margin: "-80px" }}
    transition={{ duration, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);
